import { MoreHorizontal } from 'lucide-react';
import type { ChannexQuarantinedRevision } from '@/shared/api';
import { ActionMenu, Button, Pill } from '@/shared/components';
import { OtaLogo } from '@/features/properties/OtaLogo';
import { cn } from '@/shared/lib/cn';
import { formatDateRange, formatDateTime } from '@/shared/lib/format';
import { QUARANTINE_REASON, holdReason, revisionStatusLabel } from './quarantineLabels';
import { channelLabel, holdStateLabel } from './revisionHoldPresentation';
import { RevisionHoldReplayAction } from './RevisionHoldReplayAction';
import type { ReplayRowState } from './useRevisionReplay';

interface Props {
  row: ChannexQuarantinedRevision;
  /** Der Server versucht wartende Revisionen selbst. */
  automaticRetry: boolean;
  replayState: ReplayRowState | undefined;
  onReplay: () => void;
  /** Fehlt ohne das Recht dazu; dann gibt es das Menü nicht. */
  onReleaseNights?: () => void;
}

/**
 * Eine Kanalbuchung ohne Buchung als Arbeitszeile: woran man sie beim Kanal findet, ihr Zustand,
 * der Grund im Klartext und darunter höchstens eine nächste Aktion. Die Freigabe der Nächte liegt
 * im Menü — sie ist das Gegenteil der Wiedervorlage und braucht eine eigene Bestätigung. Der
 * Kopf trägt Kanal, Nummer, Zustand und Menü, damit der Text darunter die volle Breite hat.
 */
export function RevisionHoldRow({
  row,
  automaticRetry,
  replayState,
  onReplay,
  onReleaseNights,
}: Props) {
  const waiting = row.processingState === 'AwaitingRoute';
  const outcome = replayState?.kind === 'done' ? replayState.result.outcome : null;
  const resolved = outcome === 'Applied' || outcome === 'Superseded';
  return (
    <li
      className={cn(
        'bg-card border-l-3 px-4 py-3.5',
        resolved ? 'border-l-sage' : waiting ? 'border-l-amber' : 'border-l-sealwax',
      )}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          {row.details && <OtaLogo channel={row.details.channel} size={20} />}
          <h3 className="text-ink wrap-break-word min-w-0 font-serif text-[17px]">{title(row)}</h3>
          {resolved ? (
            <Pill variant={outcome === 'Applied' ? 'live' : 'default'} size="sm">
              {outcome === 'Applied' ? 'Übernommen' : 'Überholt'}
            </Pill>
          ) : (
            <Pill variant={waiting ? 'warning' : 'stop'} size="sm">
              {holdStateLabel(row.processingState)}
            </Pill>
          )}
        </div>
        {!waiting && !resolved && onReleaseNights && (
          <ActionMenu
            trigger={
              <Button
                variant="ghost"
                size="icon"
                className="-my-2 min-h-11 min-w-11"
                aria-label="Weitere Aktionen"
              >
                <MoreHorizontal size={18} aria-hidden />
              </Button>
            }
            items={[
              {
                label: 'Nächte freigeben',
                icon: 'CalendarDays',
                destructive: true,
                onClick: onReleaseNights,
              },
            ]}
          />
        )}
      </div>
      <p className="text-ink-soft mt-1 text-[13px]">{facts(row).join(' · ')}</p>
      <p className="text-ink mt-1.5 text-sm">{holdReason(row.quarantineCode, row)}</p>
      {/* Der Rohcode nur zusätzlich zur Erklärung — bei einem unbekannten Code IST er bereits
          die Erklärung und stünde sonst zweimal da. */}
      {row.quarantineCode && QUARANTINE_REASON[row.quarantineCode] && (
        <p className="text-ink-faint mt-0.5 font-mono text-[11px]">{row.quarantineCode}</p>
      )}
      {waiting && !resolved && (
        <p className="text-ink-soft mt-1 text-[13px]">{waitText(row, automaticRetry)}</p>
      )}
      {row.replayAttempts > 0 && row.lastReplayError && (
        <p className="text-ink-faint mt-1 text-[12px]">{attemptsText(row)}</p>
      )}
      {!waiting && <RevisionHoldReplayAction row={row} state={replayState} onReplay={onReplay} />}
    </li>
  );
}

/** Eine Frist nur, solange der Server selbst versucht und sie noch läuft. */
function waitText(row: ChannexQuarantinedRevision, automaticRetry: boolean): string {
  if (!automaticRetry) {
    return 'Die automatische Wiederholung ist ausgeschaltet — die Buchung wartet, bis sie wieder läuft.';
  }
  return row.waitDeadline
    ? `Wird automatisch erneut versucht, spätestens bis ${formatDateTime(row.waitDeadline)} — danach steht sie hier in der Quarantäne.`
    : 'Wird gleich automatisch erneut versucht; scheitert das, steht sie hier in der Quarantäne.';
}

/**
 * Derselbe Grund steht schon darüber — dann genügt die Zahl der Versuche. Kann eine Revision in
 * der Quarantäne nicht erneut verarbeitet werden, nennt die Zeile darunter, worauf sie wartete.
 */
function attemptsText(row: ChannexQuarantinedRevision): string {
  const attempts = row.replayAttempts === 1 ? '1 Versuch' : `${row.replayAttempts} Versuche`;
  if (row.lastReplayError === row.quarantineCode) return `${attempts}, zuletzt aus demselben Grund`;
  const label = row.processingState === 'Quarantined' && !row.canReplay ? 'zuvor' : 'zuletzt';
  return `${attempts} · ${label}: ${holdReason(row.lastReplayError, row)}`;
}

function title(row: ChannexQuarantinedRevision): string {
  if (!row.details) return 'Kanalbuchung';
  const channel = channelLabel(row.details.channel);
  return row.details.otaReservationCode ? `${channel} ${row.details.otaReservationCode}` : channel;
}

function facts(row: ChannexQuarantinedRevision): string[] {
  const stay =
    row.details?.arrivalDate && row.details.departureDate
      ? formatDateRange(row.details.arrivalDate, row.details.departureDate)
      : null;
  return [
    row.propertyGroupName ?? 'ohne Objekt',
    stay ?? (row.details ? 'Zeitraum unbekannt' : 'Angaben zur Buchung nicht lesbar'),
    revisionStatusLabel(row.revisionStatus),
    `eingegangen ${formatDateTime(row.receivedAt)}`,
  ];
}
